import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  ArrowRight, ArrowLeft, Loader2, AlertCircle, CheckCircle2, Sparkles,
  ShieldCheck, Mail, FileSignature,
} from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import useDocumentTitle from "../hooks/useDocumentTitle";
import { trackEvent } from "../lib/analytics";
import usePageMeta from "../hooks/usePageMeta";
import M from "../lib/pageMetaConfig";

import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "../components/ui/select";

const API = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");

const CHAINS = [
  "Ethereum", "Arbitrum", "Optimism", "Base", "Polygon", "BNB Chain",
  "Avalanche", "Sonic", "Solana", "Sui", "Aptos", "TON", "Starknet",
  "Cosmos / CosmWasm", "Stellar Soroban", "Algorand", "Stacks", "Other",
];
const TIMELINES = [
  { value: "asap",      label: "ASAP (within 1 week)" },
  { value: "2-4 weeks", label: "2–4 weeks" },
  { value: "1-2 months",label: "1–2 months" },
  { value: "flexible",  label: "Flexible / no hard deadline" },
];

export default function AuditTalkToSales() {
  usePageMeta(M.auditTalkToSales);
  const [params] = useSearchParams();
  // Source attribution — set by upstream pages via ?source=... so PostHog
  // can resolve the full pre-signup funnel:
  //   pricing-custom-enterprise-tile  → from /pricing CTA
  //   custom-enterprise-page          → from /account/custom-enterprise (authed)
  //   pricing-footer                  → consult-modal opened from /pricing
  //   undefined                       → direct link / external referrer
  const source = params.get("source") || "direct";
  // Same heuristic the rest of the funnel uses: only the custom-build
  // sources express a custom_build intent. Any other source is a
  // generic audit-sales lead.
  const expectedTrack = source.includes("custom-enterprise") ? "custom_build" : "subscription";

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(null);             // {lead_id} on success
  const [form, setForm] = useState({
    name: "",
    email: "",
    company: "",
    project_name: "",
    chain: "Ethereum",
    estimated_loc: "",
    timeline: "2-4 weeks",
    repo_url: "",
    message: "",
  });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Form-view event — fires once on mount. Lets PostHog measure the
  // tile-click → form-view drop-off precisely. Tracks the source so
  // each upstream entry point gets its own conversion column.
  useEffect(() => {
    trackEvent("talk_to_sales_viewed", {
      source,
      expected_track: expectedTrack,
      path: "/audit/talk-to-sales",
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function submit(e) {
    e.preventDefault();
    setError("");
    if (!form.name.trim() || !form.email.trim() || !form.company.trim() || !form.project_name.trim()) {
      setError("Name, email, company, and project name are required.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        // Coerce LOC to number or drop it
        estimated_loc: form.estimated_loc ? Number(form.estimated_loc) : undefined,
      };
      const r = await fetch(`${API}/api/audit/sales-lead`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const body = await r.json();
      if (!r.ok) {
        setError(body.detail || "Couldn't submit. Try again or email hello@qortrace.com.");
        // Also surface failures in PostHog so we can see where forms
        // are abandoned for technical (vs intent) reasons.
        trackEvent("talk_to_sales_error", {
          source,
          expected_track: expectedTrack,
          status: r.status,
          detail: body?.detail || null,
        });
      } else {
        setDone(body);
        // Submission success — the bottom-of-funnel event for the
        // pre-signup conversion. Pairs with `enterprise_track_intent`
        // (top) to give a complete tile-click → submitted measurement.
        trackEvent("talk_to_sales_submitted", {
          source,
          expected_track: expectedTrack,
          chain: form.chain,
          timeline: form.timeline,
          has_repo: Boolean(form.repo_url),
          estimated_loc: payload.estimated_loc || null,
          lead_id: body?.lead_id || null,
        });
      }
    } catch (e) {
      setError(`Network error: ${e.message}`);
      trackEvent("talk_to_sales_error", {
        source,
        expected_track: expectedTrack,
        network: true,
        error_msg: e.message,
      });
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="min-h-screen flex flex-col" style={{ background: "var(--qt-bg)" }}>
      <Header />
      <main className="flex-1">
        <section className="max-w-[860px] mx-auto px-6 sm:px-8 pt-12 pb-6">
          <Link to="/pricing/audits" data-testid="sales-back-link"
                className="qt-mono text-xs inline-flex items-center gap-1"
                style={{ color: "var(--qt-text-3)", letterSpacing: "0.18em" }}>
            <ArrowLeft size={12} /> AUDIT PRICING
          </Link>

          <div className="qt-label mt-6">DEEP DIVE · TALK TO SALES</div>
          <h1 className="qt-display text-4xl sm:text-5xl mt-3" style={{ color: "#fff" }}
              data-testid="sales-headline">
            Get a quote for your audit.
          </h1>
          <p className="mt-4 text-base" style={{ color: "var(--qt-text-2)", lineHeight: 1.6 }}>
            Tell us about your project. Senior auditor will reply within 1
            business day with a scope, timeline, and quote. Standard NDA on
            request — your code stays yours.
          </p>

          {/* iter177 — NDA shortcut for prospects who want paperwork done before the call */}
          <div className="qt-card mt-5" data-testid="sales-nda-strip"
               style={{ padding: 14, border: "1px solid rgba(0,229,255,0.30)", background: "rgba(0,229,255,0.04)" }}>
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="flex items-start gap-3 min-w-0">
                <FileSignature size={18} style={{ color: "var(--qt-cyan)", marginTop: 2, flexShrink: 0 }} />
                <div className="min-w-0">
                  <div className="qt-label" style={{ color: "var(--qt-cyan)" }}>OPTIONAL · BEFORE THE CALL</div>
                  <p className="text-sm mt-1" style={{ color: "var(--qt-text-2)" }}>
                    Prefer to have the paperwork done before we talk? Draft
                    a mutual NDA from your account and we&rsquo;ll
                    counter-sign within one business day.
                  </p>
                </div>
              </div>
              <Link to="/account/nda?view=create"
                    data-testid="sales-nda-cta"
                    className="qt-btn qt-btn-secondary"
                    style={{ fontSize: 13, whiteSpace: "nowrap" }}>
                Draft mutual NDA <ArrowRight size={13} />
              </Link>
            </div>
          </div>
        </section>

        <section className="max-w-[860px] mx-auto px-6 sm:px-8 pb-16">
          {done ? (
            <div className="qt-card" data-testid="sales-done-card"
                 style={{
                   border: "1px solid #1FDD83",
                   background: "linear-gradient(135deg, rgba(0,210,106,0.10), transparent 60%)",
                   padding: "2rem",
                 }}>
              <div className="flex items-start gap-3">
                <CheckCircle2 size={26} style={{ color: "#1FDD83" }} />
                <div>
                  <div className="qt-label" style={{ color: "#1FDD83" }}>SUBMITTED</div>
                  <h2 className="qt-display text-2xl mt-2" style={{ color: "#fff" }}>
                    Thanks — we'll be in touch within 1 business day.
                  </h2>
                  <p className="text-sm mt-3" style={{ color: "var(--qt-text-2)", lineHeight: 1.6 }}>
                    Reference ID: <span className="qt-mono" style={{ color: "var(--qt-cyan)" }}>{done.lead_id}</span>
                    <br />
                    In the meantime, you can run a free preview of your contract
                    to see how the AI engine performs on your code.
                  </p>
                  <div className="flex gap-2 mt-5 flex-wrap">
                    <Link to="/audit/preview" data-testid="sales-done-preview"
                          className="qt-btn qt-btn-primary"
                          style={{ fontSize: 13 }}>
                      <Sparkles size={13} /> Run a free preview
                    </Link>
                    <Link to="/" data-testid="sales-done-home"
                          className="qt-btn qt-btn-secondary"
                          style={{ fontSize: 13 }}>
                      Back home
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <form onSubmit={submit} data-testid="sales-form" className="space-y-4">
              {/* Contact block */}
              <div className="qt-card">
                <div className="qt-label mb-3">CONTACT</div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <Field label="Your name" required>
                    <input className="qt-input" required
                           data-testid="sales-name"
                           value={form.name} onChange={(e) => set("name", e.target.value)} />
                  </Field>
                  <Field label="Email" required>
                    <input className="qt-input" required type="email"
                           data-testid="sales-email"
                           value={form.email} onChange={(e) => set("email", e.target.value)} />
                  </Field>
                  <Field label="Company / DAO / studio" required>
                    <input className="qt-input" required
                           data-testid="sales-company"
                           value={form.company} onChange={(e) => set("company", e.target.value)} />
                  </Field>
                  <Field label="Project name" required>
                    <input className="qt-input" required
                           data-testid="sales-project"
                           value={form.project_name}
                           onChange={(e) => set("project_name", e.target.value)} />
                  </Field>
                </div>
              </div>

              {/* Project block */}
              <div className="qt-card">
                <div className="qt-label mb-3">PROJECT</div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <Field label="Primary chain">
                    <Select value={form.chain} onValueChange={(v) => set("chain", v)}>
                      <SelectTrigger className="qt-input"
                                     data-testid="sales-chain"
                                     style={{ height: "auto", padding: "0.6rem 0.75rem" }}>
                        <SelectValue placeholder="Select a chain" />
                      </SelectTrigger>
                      <SelectContent>
                        {CHAINS.map((c) => (
                          <SelectItem key={c} value={c} data-testid={`sales-chain-opt-${c}`}>{c}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </Field>
                  <Field label="Timeline">
                    <Select value={form.timeline} onValueChange={(v) => set("timeline", v)}>
                      <SelectTrigger className="qt-input"
                                     data-testid="sales-timeline"
                                     style={{ height: "auto", padding: "0.6rem 0.75rem" }}>
                        <SelectValue placeholder="Select a timeline" />
                      </SelectTrigger>
                      <SelectContent>
                        {TIMELINES.map((t) => (
                          <SelectItem key={t.value} value={t.value}
                                      data-testid={`sales-timeline-opt-${t.value}`}>
                            {t.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </Field>
                  <Field label="Estimated lines of code"
                         hint="Rough is fine — we'll scope after a quick call.">
                    <input className="qt-input" type="number" min="0"
                           data-testid="sales-loc"
                           value={form.estimated_loc}
                           onChange={(e) => set("estimated_loc", e.target.value)} />
                  </Field>
                  <Field label="Repo URL"
                         hint="Optional — public or private (NDA on request).">
                    <input className="qt-input"
                           data-testid="sales-repo"
                           value={form.repo_url}
                           onChange={(e) => set("repo_url", e.target.value)}
                           placeholder="https://github.com/owner/repo" />
                  </Field>
                </div>
                <div className="mt-3">
                  <Field label="Anything else we should know?"
                         hint="Threat model concerns, prior audits, deadlines, integrations…">
                    <textarea className="qt-input"
                              data-testid="sales-message"
                              rows={5}
                              value={form.message}
                              onChange={(e) => set("message", e.target.value)}
                              style={{ fontFamily: "inherit" }} />
                  </Field>
                </div>
              </div>

              {error && (
                <div className="qt-card" data-testid="sales-error"
                     style={{ border: "1px solid #FF6E6E" }}>
                  <div className="flex items-start gap-2">
                    <AlertCircle size={16} style={{ color: "#FF6E6E", marginTop: 2 }} />
                    <p className="text-sm" style={{ color: "#FF6E6E" }}>{error}</p>
                  </div>
                </div>
              )}

              <div className="flex justify-between items-center flex-wrap gap-3">
                <a href="mailto:hello@qortrace.com"
                   data-testid="sales-mailto"
                   className="qt-mono text-xs inline-flex items-center gap-1"
                   style={{ color: "var(--qt-text-3)", letterSpacing: "0.10em" }}>
                  <Mail size={11} /> Or email hello@qortrace.com
                </a>
                <button type="submit" disabled={submitting}
                        data-testid="sales-submit"
                        className="qt-btn qt-btn-primary"
                        style={{ fontSize: 14, padding: "0.75rem 1.5rem" }}>
                  {submitting
                    ? <><Loader2 size={14} className="animate-spin" /> Submitting…</>
                    : <>Get a quote <ArrowRight size={14} /></>}
                </button>
              </div>
            </form>
          )}
        </section>
      </main>
      <Footer />
    </div>
  );
}

function Field({ label, hint, required, children }) {
  return (
    <label className="block">
      <div className="qt-mono text-[10px] mb-1.5"
           style={{ color: "var(--qt-text-3)", letterSpacing: "0.18em" }}>
        {label.toUpperCase()}{required && <span style={{ color: "#FF6E6E" }}> *</span>}
      </div>
      {children}
      {hint && (
        <div className="qt-mono text-[10px] mt-1"
             style={{ color: "var(--qt-text-3)" }}>
          {hint}
        </div>
      )}
    </label>
  );
}
